import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useState, useRef, memo, useMemo, useEffect } from "react";
import * as THREE from 'three';
import Ceiling from "../Ceiling";
import { BuilderProduct, BuilderRoomAsset, BuilderRoomAssets, DragSession, TwoDMesh, TwoDRoomLayout } from "@/store/builderSlice";
import BuilderSelect from "@/store/builderStoreSelector";
import { actions as builderActions } from "@/store/builderSlice";
import { useFrame, useThree } from "@react-three/fiber";
import { CSG } from "three-csg-ts";
import { Line, useGLTF } from "@react-three/drei";
import { DimensionalViewType, ProductPlacementType, SurfaceType } from "@/utils/builder/builderConstants";
import { loadGLTF } from "@/utils/builder/floorTextureUtils";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { DoorOrWindowMesh, ROOM_ASSET_OFFSETS } from "../Walls";
import ThreeDLayoutWallRenderer from "./ThreeDLayoutWallRenderer";
import React from "react";


export interface ThreeDLayoutWallsRendererProps {
    walls: TwoDMesh[];
}

const ThreeDLayoutWallsRenderer: React.FC<ThreeDLayoutWallsRendererProps> = (
    { walls }) => {
    const [roomAssetsLoaded, setRoomAssetsLoaded] = useState(false);
    const roomBoundingBox = useRef<undefined | THREE.Box3>();
    const alignmentAngle = useRef(0);
    const roomRotationX = useRef(0);
    const verticesMap = useRef<{ [key: string]: THREE.Vector3[] }>({});
    const scene = useThree((state) => state.scene);
    const wallsGroup = useRef<THREE.Group>(null);
    const floorMeshRef = useRef<THREE.Mesh | null>(null);
    const wallComps = useRef<JSX.Element[]>([]);
    const roomAssetTypesRef = useRef<string[]>([]);
    const roomAssetTypeModelsRef = useRef<{ [key: string]: THREE.Group }>({});
    const doorAndWindowMeshesRef = useRef<THREE.Mesh[]>([]);
    const wallMeshesRef = useRef<THREE.Mesh[]>([]);
    const floorGeometryRef = useRef<THREE.ShapeGeometry | null>(null);
    const wallColorMapRef = useRef<{ [key: string]: { color: string, dimensions: number[] } }>({});
    const windowTypeMapRef = useRef<{ [key: string]: string }>({});
    const doorTypeMapRef = useRef<{ [key: string]: string }>({});
    const roomCenter = useRef<THREE.Vector3>();
    const roomAssets: BuilderRoomAssets = BuilderSelect.roomAssets();
    const products: BuilderProduct[] = BuilderSelect.productContent().products;
    const urlParams: { [key: string]: string } = BuilderSelect.ui().urlParams;
    const isEditFlow = urlParams.projectId != null;
    const defaultDoorType = 'Hinged';
    const defaultWindowType = 'Hung';
    const dispatch = useDispatch();
    const meter_factor = 0.3048;
    const curProduct = products.find(product => product.productId === urlParams.productId);
    const defaultProductAdded = useRef(false);

    const checkIfAllRoomAssetsLoaded = () => {
        if (roomAssetTypesRef.current.length === Object.keys(roomAssetTypeModelsRef.current).length) {
            walls.forEach(wallMesh => {
                const doorMeshes: DoorOrWindowMesh[] = [];
                const windowMeshes: DoorOrWindowMesh[] = [];
                wallMesh.children.forEach(doorOrWindowMesh => {
                    if (doorOrWindowMesh.type === 'window') {
                        let type = windowTypeMapRef.current[doorOrWindowMesh.id];
                        let assetModel = roomAssetTypeModelsRef.current['window_' + type];
                        if (!assetModel) {
                            type = defaultWindowType;
                        }
                        assetModel = roomAssetTypeModelsRef.current['window_' + type];
                        windowMeshes.push({
                            mesh: doorOrWindowMesh,
                            asset: {
                                model: assetModel,
                                offsets: ROOM_ASSET_OFFSETS['window_' + type]
                            }
                        });
                    }

                    if (doorOrWindowMesh.type === 'door') {
                        let type = doorTypeMapRef.current[doorOrWindowMesh.id];
                        let assetModel = roomAssetTypeModelsRef.current['door_' + type];
                        if (!assetModel) {
                            type = defaultDoorType;
                        }
                        assetModel = roomAssetTypeModelsRef.current['door_' + type];
                        doorMeshes.push({
                            mesh: doorOrWindowMesh,
                            asset: {
                                model: assetModel,
                                offsets: ROOM_ASSET_OFFSETS['door_' + type]
                            }
                        });
                    }
                });
                wallComps.current.push(
                    <ThreeDLayoutWallRenderer key={ uuidv4() }
                        wallMesh={wallMesh}
                        doorMeshes={doorMeshes}
                        windowMeshes={windowMeshes}
                    />
                );
                dispatch(builderActions.updateResourceLoadStatus({
                    name: 'roomAssetModels',
                    status: 'Success',
                }));
            });
            // dispatch(builderActions.updateResourceLoadStatus({
            //     name: currentRoomModel!.roomId,
            //     status: 'Success',
            // }));
            // props.roomModelLoaded(roomBoundingBox.current, floorMeshRef.current ? (floorMeshRef.current as THREE.Mesh).geometry : floorGeometryRef.current, floorMeshRef.current);
            setRoomAssetsLoaded(true);
        }
    }
    const onRoomAssetLoaded = (assetType: string, gltf: GLTF) => {
        const scene = gltf.scene;
        // scene.traverse((child: any) => {
        //     if (child.isMesh && child.material) {
        //         (child.material as THREE.MeshBasicMaterial).color = new THREE.Color('#FFFFFF');
        //         (child.material as THREE.MeshBasicMaterial).needsUpdate = true;
        //     }
        // })
        roomAssetTypeModelsRef.current[assetType] = scene;
        checkIfAllRoomAssetsLoaded();
    }

    const loadRoomAssetModels = (assetTypes: string[], assets: BuilderRoomAsset[], category: string) => {
        assetTypes.forEach(assetType => {
            const roomAsset = assets.find(roomAsset =>
                roomAsset.type === assetType && roomAsset.threeDimentionalUrl != null
            );
            if (roomAsset) {
                roomAssetTypesRef.current.push(assetType);
                loadGLTF(roomAsset.threeDimentionalUrl, onRoomAssetLoaded.bind(this, category + '_' + roomAsset.type));
            }
        });
    }

    const addDefaultProduct = () => {
        if (!isEditFlow && curProduct && roomCenter.current && !defaultProductAdded.current) {
            defaultProductAdded.current = true;
            dispatch(builderActions.addProductToProject({
                productId: curProduct.productId,
                surfaceType: SurfaceType.FLOOR,
                surfaceId: SurfaceType.FLOOR,
                type: ProductPlacementType.Product,
                undoable: true,
                position: roomCenter.current
            }));
        }
    }
    useEffect(() => {
        let doorTypes: string[] = [], windowTypes: string[] = [];
        let posX = 0, posY = 0;
        walls.forEach(wallMesh => {
            if (wallMesh.id === 'wall0' || wallMesh.id === 'wall2') {
                posY += wallMesh.position.y;
            }
            if (wallMesh.id === 'wall1' || wallMesh.id === 'wall3') {
                posX += wallMesh.position.x;
            }

            wallMesh.children.forEach((mesh: TwoDMesh) => {
                mesh.type === 'door' && (doorTypeMapRef.current[mesh.id] = mesh.subType!);
                mesh.type === 'window' && (windowTypeMapRef.current[mesh.id] = mesh.subType!);
            });

            doorTypeMapRef.current['default_door_type'] = defaultDoorType;
            windowTypeMapRef.current['default_window_type'] = defaultWindowType;

            doorTypes = Array.from(new Set(Object.values(doorTypeMapRef.current)));
            windowTypes = Array.from(new Set(Object.values(windowTypeMapRef.current)));
        });
        roomCenter.current = new THREE.Vector3(posX / 2 * meter_factor, 0, posY / 2 * meter_factor);
        addDefaultProduct();
        dispatch(builderActions.updateResourceLoadStatus({
            name: 'roomAssetModels',
            status: 'InProgress',
        }));
        loadRoomAssetModels(doorTypes, roomAssets.doorTypes, 'door');
        loadRoomAssetModels(windowTypes, roomAssets.windowTypes, 'window');
    }, []);

    useEffect(() => {
        addDefaultProduct();
    }, [curProduct]);
    if (!roomAssetsLoaded) return <></>
    console.log('debug- wall render walls');
    return <>
        {wallComps.current}
    </>


}

export default memo(ThreeDLayoutWallsRenderer);